// SketchCanvas.jsx

import React, { Suspense, lazy, useMemo } from 'react';

import './SketchCanvas.css';

const Excalidraw = lazy(async () => {
  const module = await import('@excalidraw/excalidraw');
  await import('@excalidraw/excalidraw/index.css');
  return { default: module.Excalidraw };
});

const UI_OPTIONS = Object.freeze({
  canvasActions: {
    changeViewBackgroundColor: false,
    clearCanvas: false,
    export: false,
    loadScene: false,
    saveAsImage: false,
    saveToActiveFile: false,
    toggleTheme: false,
  },
  tools: {
    image: true,
  },
  welcomeScreen: false,
});

const FREEDRAW_TOOL = Object.freeze({
  customType: null,
  lastActiveTool: null,
  locked: false,
  type: 'freedraw',
});

/**
 * Pencil-only ink on iPad. Stay off on desktop — otherwise the mouse only pans.
 *
 * @returns {boolean}
 */
function shouldEnablePenMode() {
  if (typeof navigator === 'undefined') return false;
  const userAgent = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(userAgent)) return true;
  return /Macintosh/i.test(userAgent) && Number(navigator.maxTouchPoints) > 1;
}

/**
 * SketchCanvas
 *
 * Chrome-free Excalidraw paper. Appdeck owns tools outside this surface.
 *
 * @param {object} props
 * @param {object} [props.initialData]
 * @param {function} [props.onApi]
 * @param {boolean} [props.viewModeEnabled]
 * @returns {ReactNode}
 */
const SketchCanvas = ({
  initialData = null,
  onApi = null,
  viewModeEnabled = false,
}) => {
  const resolvedInitialData = useMemo(() => {
    const source = initialData && typeof initialData === 'object' ? initialData : {};
    return {
      appState: {
        currentItemStrokeColor: '#1e1e1e',
        gridModeEnabled: false,
        ...(source.appState || {}),
        activeTool: FREEDRAW_TOOL,
        penMode: shouldEnablePenMode(),
        theme: 'light',
        viewBackgroundColor: '#ffffff',
      },
      elements: Array.isArray(source.elements) ? source.elements : [],
      files: source.files && typeof source.files === 'object' ? source.files : {},
      scrollToContent: false,
    };
  }, [initialData]);

  return (
    <div className="sketch-canvas-container">
      <Suspense fallback={<div className="sketch-canvas-loading">Loading canvas…</div>}>
        <Excalidraw
          autoFocus
          excalidrawAPI={onApi}
          handleKeyboardGlobally
          initialData={resolvedInitialData}
          theme="light"
          UIOptions={UI_OPTIONS}
          viewModeEnabled={viewModeEnabled}
          zenModeEnabled
          validateEmbeddable={() => false}
        />
      </Suspense>
    </div>
  );
};

SketchCanvas.displayName = 'SketchCanvas';

export default SketchCanvas;
